import { useCallback, useSyncExternalStore } from "react";
import {
  queryOptions,
  useQuery,
  type QueryClient,
  type UseMutationOptions,
  useMutation,
  useIsMutating,
  useQueryClient,
} from "@tanstack/react-query";
import type { Options } from "@hey-api/client-fetch";
import type {
  PostV1OrganizationsByOrganizationIdSemanticMatchData,
  PostV1OrganizationsByOrganizationIdSemanticMatchError,
  PostV1OrganizationsByOrganizationIdSemanticMatchResponse,
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchData,
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchError,
  PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchResponse,
} from "../../client";
import {
  getV1OrganizationsByOrganizationIdLeadsByLeadIdMatchResultsOptions,
  postV1OrganizationsByOrganizationIdSemanticMatchMutation,
  postV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchMutation,
} from "../../client/@tanstack/react-query.gen";
import { QueryConfig } from "../utils/query-config";

// --- GET match results ---

export const getMatchResultsOptions = (
  organizationId: string,
  leadId: string,
) =>
  queryOptions({
    ...getV1OrganizationsByOrganizationIdLeadsByLeadIdMatchResultsOptions({
      path: { organizationID: organizationId, leadID: leadId },
    }),
  });

export const getMatchResultsQueryKey = (
  organizationId: string,
  leadId: string,
) => getMatchResultsOptions(organizationId, leadId).queryKey;

export const useGetMatchResults = (
  organizationId: string,
  leadId: string,
  config?: QueryConfig,
) => {
  return useQuery({
    ...getMatchResultsOptions(organizationId, leadId),
    enabled: !!leadId,
    retry: false,
    throwOnError: false,
    ...config,
  });
};

// --- POST semantic match (starts background job) ---

export const SEMANTIC_MATCH_MUTATION_KEY = ["semanticMatch"] as const;

export const useIsSemanticMatching = (leadId?: string) =>
  useIsMutating({
    mutationKey: SEMANTIC_MATCH_MUTATION_KEY,
    predicate: leadId
      ? (mutation) =>
          (mutation.state.variables as Options<PostV1OrganizationsByOrganizationIdSemanticMatchData>)
            ?.body?.leadId === leadId
      : undefined,
  }) > 0;

export const useSemanticMatch = (
  options: UseMutationOptions<
    PostV1OrganizationsByOrganizationIdSemanticMatchResponse,
    PostV1OrganizationsByOrganizationIdSemanticMatchError,
    Options<PostV1OrganizationsByOrganizationIdSemanticMatchData>
  > = {},
) => {
  return useMutation({
    mutationKey: SEMANTIC_MATCH_MUTATION_KEY,
    ...postV1OrganizationsByOrganizationIdSemanticMatchMutation(),
    ...options,
  });
};

// --- Streaming progress state ---

export interface MatchProgress {
  embeddingDone: boolean;
  profilesScored: number;
  totalProfiles: number;
}

const STORAGE_KEY = "smc-in-progress";

interface StoredLeadEntry {
  jobId: string;
  phase: "parsing" | "matching";
}

interface MatchingState {
  leadIds: Record<string, boolean>;
  /** leadIds that were restored from sessionStorage (no live SSE connection) */
  restoredLeadIds: Record<string, boolean>;
  progress: Record<string, MatchProgress>;
  phase: Record<string, "parsing" | "matching">;
  /**
   * leadIds whose stored phase was "parsing" but the server job is now gone —
   * meaning parsing completed while the page was reloaded. The home page should
   * auto-trigger semantic matching for these, then call clearNeedsMatch.
   */
  needsMatch: Record<string, boolean>;
}

function _loadFromStorage(): Pick<MatchingState, "leadIds" | "restoredLeadIds" | "phase"> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { leadIds: {}, restoredLeadIds: {}, phase: {} };
    const stored: Record<string, StoredLeadEntry> = JSON.parse(raw);
    const leadIds: Record<string, boolean> = {};
    const phase: Record<string, "parsing" | "matching"> = {};
    for (const [leadId, entry] of Object.entries(stored)) {
      leadIds[leadId] = true;
      phase[leadId] = entry.phase;
    }
    return { leadIds, restoredLeadIds: { ...leadIds }, phase };
  } catch {
    return { leadIds: {}, restoredLeadIds: {}, phase: {} };
  }
}

// Module-level store — survives component mount/unmount cycles; seeded from sessionStorage on load
let _matchingState: MatchingState = { progress: {}, needsMatch: {}, ..._loadFromStorage() };

// jobId stored per leadId for reconnecting
const _storedEntries = new Map<string, StoredLeadEntry>();
(function _initStoredEntries() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored: Record<string, StoredLeadEntry> = JSON.parse(raw);
    Object.entries(stored).forEach(([leadId, entry]) => {
      _storedEntries.set(leadId, entry);
    });
  } catch {
    // ignore
  }
})();

const _listeners = new Set<() => void>();

function _notify() {
  _listeners.forEach((l) => l());
}

function _saveToStorage() {
  try {
    const out: Record<string, StoredLeadEntry> = {};
    _storedEntries.forEach((entry, leadId) => {
      out[leadId] = entry;
    });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(out));
  } catch {
    // ignore
  }
}

function _setActive(leadId: string, jobId: string, phase: "parsing" | "matching", progress?: MatchProgress) {
  const { [leadId]: _r, ...restRestored } = _matchingState.restoredLeadIds;
  const { [leadId]: _n, ...restNeedsMatch } = _matchingState.needsMatch;
  _matchingState = {
    leadIds: { ..._matchingState.leadIds, [leadId]: true },
    restoredLeadIds: restRestored,
    needsMatch: restNeedsMatch,
    progress: {
      ..._matchingState.progress,
      [leadId]: progress ?? { embeddingDone: false, profilesScored: 0, totalProfiles: 0 },
    },
    phase: { ..._matchingState.phase, [leadId]: phase },
  };
  _storedEntries.set(leadId, { jobId, phase });
  _saveToStorage();
  _notify();
}

function _updateProgress(leadId: string, patch: Partial<MatchProgress>) {
  _matchingState = {
    ..._matchingState,
    progress: {
      ..._matchingState.progress,
      [leadId]: { ..._matchingState.progress[leadId], ...patch },
    },
  };
  _notify();
}

function _clearLiveStream(leadId: string) {
  const { [leadId]: _a, ...restLeadIds } = _matchingState.leadIds;
  const { [leadId]: _b, ...restProgress } = _matchingState.progress;
  const { [leadId]: _c, ...restRestored } = _matchingState.restoredLeadIds;
  const { [leadId]: _d, ...restPhase } = _matchingState.phase;
  _matchingState = { ..._matchingState, leadIds: restLeadIds, restoredLeadIds: restRestored, progress: restProgress, phase: restPhase };
  // intentionally NOT clearing _storedEntries or saving — sessionStorage stays intact for page reload
  _notify();
}

function _clearMatching(leadId: string, markNeedsMatch = false) {
  const { [leadId]: _a, ...restLeadIds } = _matchingState.leadIds;
  const { [leadId]: _b, ...restProgress } = _matchingState.progress;
  const { [leadId]: _c, ...restRestored } = _matchingState.restoredLeadIds;
  const { [leadId]: _d, ...restPhase } = _matchingState.phase;
  _matchingState = {
    ..._matchingState,
    leadIds: restLeadIds,
    restoredLeadIds: restRestored,
    progress: restProgress,
    phase: restPhase,
    needsMatch: markNeedsMatch
      ? { ..._matchingState.needsMatch, [leadId]: true }
      : _matchingState.needsMatch,
  };
  _storedEntries.delete(leadId);
  _saveToStorage();
  _notify();
}

export function clearNeedsMatch(leadId: string) {
  const { [leadId]: _n, ...restNeedsMatch } = _matchingState.needsMatch;
  _matchingState = { ..._matchingState, needsMatch: restNeedsMatch };
  _notify();
}

const _subscribe = (listener: () => void) => {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
};
const _getSnapshot = () => _matchingState;

/** Hook for components that need to read matching state (e.g. LeadRow). */
export const useMatchingState = () =>
  useSyncExternalStore(_subscribe, _getSnapshot);

// Module-level abort controllers keyed by leadId — survive component unmounts
export const sseAbortControllers = new Map<string, AbortController>();

/**
 * Opens an SSE connection to /match-stream for a lead and streams progress into _matchingState.
 * Handles progress_sync (reconnect catch-up), embedding_done, profile_scored, complete.
 */
async function openMatchStream(
  organizationId: string,
  leadId: string,
  queryClient: QueryClient,
) {
  const token = localStorage.getItem("profiletool-auth-token");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  sseAbortControllers.get(leadId)?.abort();
  const controller = new AbortController();
  sseAbortControllers.set(leadId, controller);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: getMatchResultsQueryKey(organizationId, leadId),
    });

  try {
    const response = await fetch(
      `${baseUrl}/v1/organizations/${organizationId}/leads/${leadId}/match-stream`,
      {
        headers: {
          Accept: "text/event-stream",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      },
    );

    if (!response.ok || !response.body) {
      // Job gone (404 = never existed, 410 = server restart cleared hub).
      // Clear loading state and surface any persisted results.
      _clearMatching(leadId);
      invalidate();
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    outer: while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() ?? "";

      for (const chunk of chunks) {
        const dataLine = chunk.trim();
        if (!dataLine.startsWith("data: ")) continue;
        try {
          const evt = JSON.parse(dataLine.slice(6));
          if (evt.type === "progress_sync") {
            // Reconnect catch-up — set progress directly from DB state
            const p = evt.payload;
            _updateProgress(leadId, {
              embeddingDone: p?.embeddingDone ?? false,
              profilesScored: p?.profilesScored ?? 0,
              totalProfiles: p?.total ?? 0,
            });
          } else if (evt.type === "embedding_done") {
            _updateProgress(leadId, {
              embeddingDone: true,
              totalProfiles: evt.payload?.total ?? _matchingState.progress[leadId]?.totalProfiles ?? 0,
            });
          } else if (evt.type === "profile_scored") {
            _updateProgress(leadId, {
              profilesScored: evt.payload.index,
              totalProfiles: evt.payload.total,
            });
          } else if (evt.type === "complete") {
            reader.cancel();
            break outer;
          }
        } catch {
          // ignore parse errors
        }
      }
    }

    invalidate();
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
  } finally {
    // Only clear state if this controller is still the active one.
    // A newer openMatchStream call may have replaced it — don't clobber that state.
    if (sseAbortControllers.get(leadId) === controller) {
      _clearLiveStream(leadId);
      sseAbortControllers.delete(leadId);
    }
  }
}

/**
 * Starts a semantic match job via POST, then subscribes to the SSE stream.
 */
export async function runSemanticMatchStream(
  organizationId: string,
  leadId: string,
  queryClient: QueryClient,
) {
  const token = localStorage.getItem("profiletool-auth-token");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  try {
    const response = await fetch(
      `${baseUrl}/v1/organizations/${organizationId}/semantic-match`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ leadId, limit: 6 }),
      },
    );

    if (!response.ok) return;

    const { jobId } = await response.json();
    if (!jobId) return;

    _setActive(leadId, jobId, "matching");
    await openMatchStream(organizationId, leadId, queryClient);
  } catch {
    _clearMatching(leadId);
  }
}

/**
 * Called when the leads list mounts. Fetches active jobs from the server and:
 * - For assignment-reader jobs: marks the lead as in "parsing" phase
 * - For semantic-match jobs: subscribes to the SSE stream
 * Also handles locally-stored job entries (e.g. from a reload mid-matching).
 */
export async function discoverAndSubscribeAll(
  organizationId: string,
  queryClient: QueryClient,
) {
  const token = localStorage.getItem("profiletool-auth-token");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  try {
    const response = await fetch(
      `${baseUrl}/v1/organizations/${organizationId}/active-lead-jobs`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    if (!response.ok) return;

    const jobs: Array<{ jobId: string; leadId: string; type: string; progress?: { embeddingDone?: boolean; profilesScored?: number; total?: number } }> =
      await response.json();

    const serverLeadIds = new Set<string>();

    for (const job of jobs) {
      serverLeadIds.add(job.leadId);

      if (job.type === "assignment-reader") {
        // Binary state — just mark as parsing, no SSE available
        _setActive(job.leadId, job.jobId, "parsing");
      } else if (job.type === "semantic-match") {
        const progress: MatchProgress = {
          embeddingDone: job.progress?.embeddingDone ?? false,
          profilesScored: job.progress?.profilesScored ?? 0,
          totalProfiles: job.progress?.total ?? 0,
        };
        _setActive(job.leadId, job.jobId, "matching", progress);
        // Skip if a live stream is already open for this lead (e.g. started by runSemanticMatchStream).
        // Opening a second stream would abort the first and briefly clear isMatching.
        if (!sseAbortControllers.has(job.leadId)) {
          openMatchStream(organizationId, job.leadId, queryClient);
        }
      }
    }

    // Clear stale sessionStorage entries for leads that no longer have active server jobs.
    // If the stored phase was "parsing", parsing completed while the page was reloaded —
    // mark the lead as needing auto-match so the home page can trigger it.
    const staleEntries = Array.from(_storedEntries.entries()).filter(
      ([leadId]) => !serverLeadIds.has(leadId),
    );
    for (const [leadId, entry] of staleEntries) {
      _clearMatching(leadId, entry.phase === "parsing");
    }
  } catch {
    // ignore — non-critical
  }
}

export const useSemanticMatchWithProgress = (organizationId: string, leadId: string) => {
  const queryClient = useQueryClient();
  const state = useSyncExternalStore(_subscribe, _getSnapshot);

  const isMatching = !!state.leadIds[leadId];
  const isRestored = !!state.restoredLeadIds[leadId];
  const phase = state.phase[leadId] ?? "matching";
  const progress: MatchProgress = state.progress[leadId] ?? {
    embeddingDone: false,
    profilesScored: 0,
    totalProfiles: 0,
  };

  const run = useCallback(() => {
    runSemanticMatchStream(organizationId, leadId, queryClient);
  }, [organizationId, leadId, queryClient]);

  const cancel = useCallback(() => {
    sseAbortControllers.get(leadId)?.abort();
    _clearMatching(leadId);
  }, [leadId]);

  return { run, cancel, isMatching, isRestored, progress, phase };
};

// --- POST semantic match (single profile) ---

export const useSemanticMatchProfile = (
  options: UseMutationOptions<
    PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchResponse,
    PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchError,
    Options<PostV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchData>
  > = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...postV1OrganizationsByOrganizationIdProfilesByProfileIdSemanticMatchMutation(),
    ...options,
    onSettled: (data, error, variables, context) => {
      const orgId = variables.path?.organizationID;
      const leadId = variables.body?.leadId;
      if (orgId && leadId) {
        queryClient.invalidateQueries({
          queryKey: getMatchResultsQueryKey(orgId, leadId),
        });
      }
      options.onSettled?.(data, error, variables, context);
    },
  });
};
